<template>
  <v-card>
    <v-card-title>
      <v-row align="center" class="search-field">
        <v-col md="6">
          <text-field
            class="mb-5"
            v-model="filters.searchStr"
            append-icon="mdi-filter"
            label="Filter"
            single-line
            hide-details></text-field>
        </v-col>
      </v-row>
    </v-card-title>
    <div v-if="!dataFetched" class="align-self-stretch text-center">
      <p class="mb-4 text-h6">Loading ...</p>
      <v-progress-linear color="secondary" indeterminate rounded height="8"></v-progress-linear>
    </div>
    <v-data-table
      :items="loadTypesWithFullName"
      :headers="headers"
      :loading="!dataFetched"
      :search="filters.searchStr"
      :item-class="getSelectedClass"
      :server-items-length="totalLoadTypes"
      class="load-type-select-table"
      single-expand
      show-expand
      :footer-props="footerProps"
      :options.sync="options">
      <template v-slot:[`header.actions`]></template>
      <template v-slot:[`item.name`]="{ item }">
        <v-tooltip v-if="item.name" top>
          <template v-slot:activator="{ on }">
            <span v-on="on">{{ novaCore.truncateString(item.name, 50) }}</span>
          </template>
          <span>{{ item.name }}</span>
        </v-tooltip>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <p class="pt-4"><strong>Description:</strong> {{ item.description ?? 'N/A' }}</p>
        </td>
      </template>
      <template v-slot:[`item.direction`]="{ item }">
        <span :inner-html.prop="item.direction | highlight(filters.searchStr)"></span>
      </template>
      <template v-slot:[`item.duration_min`]="{ item }">
        <span
          :inner-html.prop="`${item.duration_min} Minutes` | highlight(filters.searchStr)"></span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <primary-button @click="handleClick(item)" x-small>
          Select
          <v-icon x-small>mdi-arrow-right</v-icon>
        </primary-button>
      </template>
      <template v-slot:no-data>
        <v-alert
          v-if="dataFetched && !filters.searchStr"
          color="warning lighten-2"
          icon="mdi-alert"
          dense
          class="my-8">
          <h4>There are no load types available for scheduling at this warehouse.</h4>
        </v-alert>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import dataListMixin from '@satellite/components/mixins/dataListMixin';

export default {
  mixins: [dataListMixin],
  props: {
    /**
     * @model
     */
    value: {
      required: true
    },
    /**
     * Selected Warehouse
     */
    selectedWarehouse: {
      type: Object,
      required: false,
      default() {
        return null;
      }
    },
    /**
     * Determines if duration should show next to load type name
     */
    includeDurations: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  filters: {
    highlight(value, query) {
      if (typeof value !== 'string') {
        return value;
      }

      return query
        ? value.replace(new RegExp(query, 'ig'), v => `<span class="orange lighten-3">${v}</span>`)
        : value;
    }
  },
  data() {
    return {
      headers: [
        {
          text: 'Name',
          value: 'name',
          searchable: true,
          sortable: true
        },
        {
          text: 'Direction',
          value: 'direction',
          searchable: true,
          sortable: true
        },
        {
          text: 'Operation',
          value: 'operation',
          searchable: true,
          sortable: true
        },
        {
          text: 'Equipment Type',
          value: 'equipmentType',
          searchable: true,
          sortable: true
        },
        {
          text: 'Transportation Mode',
          value: 'transportationMode',
          searchable: true,
          sortable: true
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'end'
        }
      ],
      selectedLoadType: null,
      loadTypes: [],
      warehouseLoadTypes: [],
      sortBy: ['name', 'asc'],
      totalLoadTypes: 0,
      dataFetched: false
    };
  },
  computed: {
    loadTypesWithFullName() {
      return this.warehouseLoadTypes.map(loadType => ({
        ...loadType,
        name: this.$loadTypeName(loadType, false)
      }));
    }
  },
  methods: {
    /**
     * Fetch load types based on selected warehouse
     * @public
     * @returns {Promise<void>}
     */
    async getData() {
      this.warehouseLoadTypes = [];
      this.dataFetched = false;

      const warehouseId = this.selectedWarehouse?.id;
      if (warehouseId) {
        const query = this.getQueryBase();
        query.s.allowCarrierScheduling = true;

        query.warehouseId = warehouseId;
        query.showOnlyAssignedLoadTypes = true;

        const response = await axios
          .get('loadtype', {
            params: query
          })
          .finally(() => (this.dataFetched = true));

        if (response?.data) {
          this.warehouseLoadTypes = response.data.data;
          this.totalLoadTypes = response.data.total;
        }
      }
      this.dataFetched = true;
    },
    handleClick(loadType) {
      this.selectedLoadType = loadType;
      if (this.$warehouseId && this.$warehouseOrgId) {
        this.mixpanel.track(this.mixpanel.events.ACTION.LOAD_TYPE_SELECTED, {
          'Warehouse ID': this.$warehouseId,
          'Org ID': this.$warehouseOrgId
        });
      }
      this.$emit('input', loadType);
      this.$emit('selected');
    },
    getSelectedClass(item) {
      let selectedClass = 'load-type-select-item';
      if (item.id === this.selectedLoadType?.id) {
        selectedClass += ' is-selected';
      }
      return selectedClass;
    }
  },
  mounted() {
    // Dynamically add the load type duration based on isLoadTypeDurationCarrierViewHidden Setting
    if (!this.$warehouse.settings.isLoadTypeDurationCarrierViewHidden) {
      this.headers.splice(-1, 0, {
        text: 'Duration',
        value: 'duration_min',
        searchable: true,
        sortable: true
      });
    }
  }
};
</script>
